/**
 * The document surface's stylesheet, injected once as a real <style> tag.
 *
 * WHY NOT TAILWIND. Two reasons, both structural rather than taste:
 *
 *  1. The blots in `deltaBlots.ts` build DOM nodes imperatively, and a class
 *     that only ever exists inside a `create()` call is exactly the kind
 *     Tailwind's content scanner is least reliable about keeping.
 *  2. This package is compiled by TWO apps with DIFFERENT colour tokens (see
 *     the PALETTE RULE in `ui.tsx`). The document page must look identical in
 *     both, so its colours are literal here rather than borrowed from a
 *     palette that differs between them.
 *
 * Everything is namespaced under `.dq-` so it cannot leak into the host app.
 */

export const DELTA_EDITOR_STYLE_ID = 'dq-editor-styles';

export const DELTA_EDITOR_CSS = `
/* ── the page ────────────────────────────────────────────────────────── */
.dq-page{
  width:min(760px,100%); background:#fff; border:1px solid #e7e5e4; border-radius:3px;
  box-shadow:0 1px 2px rgba(0,0,0,.05),0 8px 24px -12px rgba(0,0,0,.13);
  margin:0 auto;
}
.dq-page .ql-container{
  font-family:'Iowan Old Style','Palatino Linotype',Palatino,Georgia,'Times New Roman',serif;
  font-size:15.5px; line-height:1.72; color:#1c1917; border:none;
}
.dq-page .ql-editor{ padding:64px 78px 72px; min-height:520px; }
.dq-page .ql-editor.ql-blank::before{
  left:78px; right:78px; color:#a8a29e; font-style:normal;
}

/* headings — the derived section number is drawn by ::before from a
   data attribute the editor stamps on each line (see DeltaEditor). */
.dq-page .ql-editor h1{ font-size:25px; line-height:1.25; font-weight:700; text-align:center;
  letter-spacing:-.015em; margin:0 0 18px; }
/* The three levels differ on SIZE, WEIGHT AND CASE at once. A size-only scale
   collapses at a glance, and that is how a sub-section gets mistaken for a
   section in a signed contract. Matches the renderers' own heading scale. */
.dq-page .ql-editor h2{ font-size:17.5px; font-weight:700; margin:26px 0 9px; position:relative;
  padding-bottom:5px; border-bottom:1.5px solid #DC143C; letter-spacing:-.005em; }
.dq-page .ql-editor h3{ font-size:13px; font-weight:700; margin:17px 0 5px; position:relative;
  color:#9f1239; text-transform:uppercase; letter-spacing:.06em; }
.dq-page .ql-editor h2[data-secno]::before,
.dq-page .ql-editor h3[data-secno]::before{
  content:attr(data-secno); position:absolute; left:-44px; width:34px; text-align:right;
  color:#DC143C; font-weight:700; font-variant-numeric:tabular-nums;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; font-size:12.5px;
  top:.32em; user-select:none; pointer-events:none;
}
.dq-page .ql-editor p{ margin:0 0 12px; }
.dq-page .ql-editor ol,.dq-page .ql-editor ul{ margin:0 0 12px; }
.dq-page .ql-editor li{ margin:0 0 5px; }
.dq-page .ql-editor a{ color:#1d4ed8; }

/* ── callout LINES (the editable kind) ───────────────────────────────────
 *
 * Each line carries its own class, so consecutive lines are styled to MERGE
 * into one box: no vertical margin between them, and the border-left runs
 * continuously down the run. That is what makes a three-line aside read as
 * one aside while every line stays independently editable.
 *
 * Colours match the .callout-* rules in the HTML renderer's stylesheet and
 * TONE in the PDF renderer, so the box on screen is the box in the artifact.
 *
 * NOTE: no backticks anywhere in this block — it lives inside a template
 * literal, so one would terminate the string mid-stylesheet.
 */
.dq-page .ql-editor .dq-cl-info,
.dq-page .ql-editor .dq-cl-warning,
.dq-page .ql-editor .dq-cl-danger{
  margin:0; padding:5px 14px; border-left:3px solid;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; font-size:14px; line-height:1.6;
}
.dq-page .ql-editor .dq-cl-info   { background:#f0f9ff; border-color:#0284c7; color:#1e3a8a; }
.dq-page .ql-editor .dq-cl-warning{ background:#fffbeb; border-color:#d97706; color:#78350f; }
.dq-page .ql-editor .dq-cl-danger { background:#fef2f2; border-color:#dc2626; color:#7f1d1d; }
/* Breathing room only at the ends of a run, so the box does not collapse
   against the paragraphs above and below it. */
.dq-page .ql-editor :not([class*="dq-cl-"]) + [class*="dq-cl-"]{ margin-top:16px; padding-top:11px; }
.dq-page .ql-editor [class*="dq-cl-"]:last-child{ padding-bottom:11px; }
.dq-page .ql-editor [class*="dq-cl-"]:has(+ :not([class*="dq-cl-"])){ padding-bottom:11px; margin-bottom:16px; }

/* ── merge-field chips ───────────────────────────────────────────────── */
.dq-chip{
  display:inline-block; border-radius:5px; padding:0 6px; border:1px solid;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:12.5px; line-height:1.55; white-space:nowrap; vertical-align:baseline;
  cursor:default; user-select:none;
}
.dq-g-company { background:#eff6ff; border-color:#bfdbfe; color:#1d4ed8; }
.dq-g-customer{ background:#ecfdf5; border-color:#a7f3d0; color:#047857; }
.dq-g-signer  { background:#fffbeb; border-color:#fde68a; color:#b45309; }
.dq-g-finance { background:#f0fdfa; border-color:#99f6e4; color:#0f766e; }
.dq-g-agree   { background:#fff1f2; border-color:#fecdd3; color:#be123c; }
.dq-g-other   { background:#f5f5f4; border-color:#d6d3d1; color:#44403c; }
/* Quill marks the selected embed; make that unmistakable on a chip. */
.dq-chip.ql-selected, .dq-xref.ql-selected{ outline:2px solid #9f1239; outline-offset:1px; }

.dq-xref{
  display:inline-block; background:#f5f5f4; border:1px solid #d6d3d1; border-radius:5px;
  padding:0 6px; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:12.5px; color:#44403c; white-space:nowrap; cursor:default; user-select:none;
}

/* ── block embeds ────────────────────────────────────────────────────── */
.dq-callout,.dq-table,.dq-sig,.dq-mfline,.dq-pagebreak,.dq-schedule{ position:relative; margin:16px 0; }
.dq-callout{ border-left:3px solid; border-radius:0 7px 7px 0; padding:11px 14px; font-size:14px;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; line-height:1.55; }
.dq-tone-info   { background:#eff6ff; border-color:#3b82f6; color:#1e3a8a; }
.dq-tone-warning{ background:#fffbeb; border-color:#f59e0b; color:#78350f; }
.dq-tone-danger { background:#fef2f2; border-color:#ef4444; color:#7f1d1d; }

.dq-table table{ width:100%; border-collapse:collapse;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; font-size:12.5px; }
.dq-table th{ background:#fafaf9; text-align:left; font-weight:600; color:#44403c; }
.dq-table th,.dq-table td{ border:1px solid #d6d3d1; padding:6px 9px; min-width:56px; }
/* A cell being typed into gets a ring, so it is obvious which one has focus
   inside an embed the surrounding editor does not manage. */
.dq-table th:focus,.dq-table td:focus{ outline:2px solid #9f1239; outline-offset:-2px; background:#fff1f2; }

/* Row/column controls: visible only while the table is hovered or focused, so
   they do not sit in the middle of a contract the rest of the time. */
.dq-tbar{ display:flex; gap:4px; margin-top:6px; opacity:0; transition:opacity .12s;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; }
.dq-table:hover .dq-tbar,.dq-table:focus-within .dq-tbar{ opacity:1; }
.dq-tbtn{ font:inherit; font-size:11px; border:1px solid #d6d3d1; background:#fff; color:#44403c;
  border-radius:5px; padding:2px 8px; cursor:pointer; }
.dq-tbtn:hover{ background:#f5f5f4; border-color:#a8a29e; }

.dq-sig{ border:1px dashed #d6d3d1; border-radius:8px; padding:15px 16px 12px; background:#fdfdfc; }
.dq-sig-cap{ font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:10.5px; letter-spacing:.08em; text-transform:uppercase; color:#a8a29e;
  margin-bottom:13px; font-weight:700; }
.dq-sig-grid{ display:grid; grid-template-columns:1fr 168px; gap:22px 22px; }
.dq-sig-line{ border-bottom:1px solid #a8a29e; height:26px; }
.dq-sig-lab{ font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:10.5px; color:#78716c; margin-top:4px; }

.dq-mfline{ font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:13.5px; color:#44403c; }
.dq-mflabel{ color:#78716c; margin-right:6px; }

/* A SCHEDULE. Reads as a table with its rows withheld, because that is what it
   is: the shape is the author's, the rows are the order's. Nothing inside is
   editable, so it deliberately shows no focus ring on its cells. */
.dq-schedule{ border:1px solid #e7e5e4; border-radius:8px; padding:12px 13px 11px;
  background:#fcfcfb; }
.dq-schedule-title{ font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:12px; font-weight:700; color:#44403c; margin-bottom:8px; }
.dq-schedule-table{ width:100%; border-collapse:collapse;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif; font-size:12.5px; }
.dq-schedule-table th{ background:#f5f5f4; text-align:left; font-weight:600; color:#44403c; }
.dq-schedule-table th,.dq-schedule-table td{ border:1px solid #e7e5e4; padding:5px 9px; }
.dq-schedule-ghost td{ color:#a8a29e; font-style:italic; }
.dq-schedule-note{ margin-top:7px; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:10.5px; color:#a8a29e; }

.dq-pagebreak{ border-top:1px dashed #d6d3d1; text-align:center; }
.dq-pagebreak span{ position:relative; top:-9px; background:#fff; padding:0 10px;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
  font-size:10px; letter-spacing:.11em; text-transform:uppercase; color:#a8a29e; font-weight:700; }

/* An embed the caret is on gets a ring, so "what will backspace delete?" is
   never a guess on an atomic object. */
.dq-callout.ql-selected,.dq-table.ql-selected,.dq-sig.ql-selected,
.dq-mfline.ql-selected,.dq-pagebreak.ql-selected,
.dq-schedule.ql-selected{ outline:2px solid #9f1239; outline-offset:2px; }

/* ── the suggestion menu ─────────────────────────────────────────────── */
.dq-menu{
  position:absolute; z-index:40; width:352px; max-height:330px; display:flex; flex-direction:column;
  background:#fff; border:1px solid #d6d3d1; border-radius:10px; overflow:hidden;
  box-shadow:0 12px 34px -8px rgba(0,0,0,.26);
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,sans-serif;
}
.dq-menu-head{ display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding:8px 11px; border-bottom:1px solid #e7e5e4; background:#fafaf9; flex:0 0 auto; }
.dq-menu-head .q{ font-size:12.5px; color:#44403c; }
.dq-menu-head .q b{ font-family:ui-monospace,Menlo,monospace; color:#9f1239; }
.dq-menu-head .ct{ font-size:11px; color:#a8a29e; }
.dq-menu-list{ overflow-y:auto; flex:1 1 auto; padding:3px 0; }
.dq-row{ display:flex; align-items:center; gap:9px; padding:6px 11px; cursor:pointer; }
.dq-row:hover{ background:#fafaf9; }
.dq-row.on{ background:#fff1f2; box-shadow:inset 2px 0 0 #9f1239; }
.dq-row .lab{ font-size:13px; color:#1c1917; flex:0 0 auto; }
.dq-row .ex{ font-size:11.5px; color:#a8a29e; margin-left:auto; font-style:italic;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:150px; }
.dq-row .ico{ width:22px; height:22px; border-radius:5px; background:#f5f5f4; color:#44403c;
  display:inline-flex; align-items:center; justify-content:center; font-size:12px; flex:0 0 auto; }
.dq-menu-foot{ border-top:1px solid #e7e5e4; padding:6px 11px; font-size:10.5px; color:#a8a29e;
  display:flex; gap:12px; background:#fafaf9; flex:0 0 auto; }
.dq-menu-foot kbd{ font-family:ui-monospace,Menlo,monospace; background:#fff;
  border:1px solid #d6d3d1; border-bottom-width:2px; border-radius:3px; padding:0 3px;
  font-size:10px; color:#44403c; }
.dq-menu-empty{ padding:11px; font-size:12px; color:#a8a29e; }

/* ── redline ─────────────────────────────────────────────────────────── */
.dq-ins{ background:#ecfdf5; color:#047857; text-decoration:underline;
  text-decoration-thickness:1.5px; text-underline-offset:2px; border-radius:2px; }
.dq-del{ background:#fef2f2; color:#b91c1c; text-decoration:line-through;
  text-decoration-thickness:1.5px; border-radius:2px; }
`;

/** Injected once per document. Idempotent — safe to call from every mount. */
export function ensureDeltaEditorStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(DELTA_EDITOR_STYLE_ID)) return;
  const tag = document.createElement('style');
  tag.id = DELTA_EDITOR_STYLE_ID;
  tag.textContent = DELTA_EDITOR_CSS;
  document.head.appendChild(tag);
}
